import {useState, useRef, useEffect} from 'react'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {useNavigate} from 'react-router-dom'
import {requestPassword, verifyOTP} from '../core/_requests'
import {useIntl} from 'react-intl'
import {useLang} from '../../../../_metronic/i18n/Metronici18n'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {brandLogo} from '../../../tenant/applyBranding'
import {
  createResetTimer,
  resetSecondsRemaining,
  resetTimerFromSearch,
  resetTimerQuery,
} from '../resetTimer'
import type {ResetTimer} from '../resetTimer'

export function VerifyOTP() {
  const {formatMessage} = useIntl()
  const lang = useLang()
  const [loading, setLoading] = useState(false)
  const [resending, setResending] = useState(false)

  const otpSchema = Yup.object().shape({
    code: Yup.string()
      .length(4, formatMessage({id: 'VALIDATION.CODE.LENGTH'}))
      .required(formatMessage({id: 'VALIDATION.REQUIRED_FIELD'})),
  })
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const navigate = useNavigate()
  const [otp, setOtp] = useState(['', '', '', ''])
  const inputRefs = useRef<Array<HTMLInputElement | null>>([])

  const urlParams = new URLSearchParams(window.location.search)
  const email = urlParams.get('email') || ''
  const [timer, setTimer] = useState<ResetTimer | null>(() => resetTimerFromSearch(urlParams))
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(() =>
    resetSecondsRemaining(timer)
  )
  const expired = remainingSeconds === 0

  useEffect(() => {
    if (!timer) {
      setRemainingSeconds(null)
      return
    }

    const updateRemainingTime = () => {
      setRemainingSeconds(resetSecondsRemaining(timer))
    }

    updateRemainingTime()
    const intervalId = window.setInterval(updateRemainingTime, 1000)
    return () => window.clearInterval(intervalId)
  }, [timer])

  const displayedSeconds = remainingSeconds || 0
  const formattedTime = `${String(Math.floor(displayedSeconds / 60)).padStart(2, '0')}:${String(
    displayedSeconds % 60
  ).padStart(2, '0')}`

  const formik = useFormik({
    initialValues: {
      code: '',
    },
    validationSchema: otpSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setHasErrors(undefined)
      try {
        const response = await verifyOTP(email, values.code, lang)
        const token = response.data.user?.token
        if (response.data.status_code === 'verified' && token) {
          setHasErrors(false)
          setLoading(false)
          navigate(`/auth/new-password?token=${token}`)
        } else {
          setHasErrors(true)
          setLoading(false)
          setSubmitting(false)
          setStatus(formatMessage({id: 'invalid_verification_code'}))
        }
      } catch (error: any) {
        setHasErrors(true)
        setLoading(false)
        setSubmitting(false)
        setStatus(error.response?.data?.message || formatMessage({id: 'invalid_verification_code'}))
      }
    },
  })

  const handleChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp]
      newOtp[index] = value
      setOtp(newOtp)

      // Combine OTP digits and update formik
      const combinedOtp = newOtp.join('')
      formik.setFieldValue('code', combinedOtp)

      // Move to next input if value is entered
      if (value !== '' && index < otp.length - 1) {
        inputRefs.current[index + 1]?.focus()
      }
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && index > 0 && otp[index] === '') {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handleResend = async () => {
    setResending(true)
    setHasErrors(undefined)
    try {
      const response = await requestPassword(email, lang)
      const newTimer = createResetTimer(response.data.expires_in)
      setTimer(newTimer)
      setRemainingSeconds(resetSecondsRemaining(newTimer))
      setOtp(['', '', '', ''])
      formik.resetForm()
      window.history.replaceState(
        null,
        '',
        `/auth/verify-otp?email=${encodeURIComponent(email)}${resetTimerQuery(newTimer)}`
      )
      window.setTimeout(() => inputRefs.current[0]?.focus(), 0)
    } catch (error: any) {
      setHasErrors(true)
      formik.setStatus(error.response?.data?.message || formatMessage({id: 'failed_to_send_code'}))
    } finally {
      setResending(false)
    }
  }

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus()
  }, [])

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_verify_otp_form'
      onSubmit={formik.handleSubmit}
    >
      <div>
        <img
          alt=''
          width='170'
          className='mb-10 mb-10 mx-auto d-block'
          src={`${brandLogo(
            lang === 'ar',
            toAbsoluteUrl('/media/logos/logo_revamp' + (lang === 'ar' ? '-ar' : '') + '.svg')
          )}`}
        />
      </div>

      <div className='text-center mb-10'>
        <h1 className='text-dark fw-bolder mb-3'>{formatMessage({id: 'AUTH.VERIFY.TITLE'})}</h1>
        <div className='text-gray-500 fw-semibold fs-6'>
          {formatMessage({id: 'AUTH.VERIFY.DESC'})}
        </div>
      </div>

      {hasErrors === true && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      )}

      {remainingSeconds !== null && (
        <div
          className={`mb-8 text-center fw-semibold ${expired ? 'text-danger' : 'text-gray-600'}`}
        >
          {expired
            ? formatMessage({id: 'AUTH.VERIFY.EXPIRED'})
            : formatMessage({id: 'AUTH.VERIFY.EXPIRES_IN'}, {time: formattedTime})}
        </div>
      )}

      <div className='mb-10 d-flex justify-content-center gap-3'>
        {otp.map((digit, index) => (
          <input
            key={index}
            type='text'
            ref={(element) => {
              inputRefs.current[index] = element
            }}
            className='form-control bg-transparent w-50px text-center fs-2'
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            maxLength={1}
            disabled={expired || loading}
          />
        ))}
      </div>

      <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
        <button
          type='submit'
          id='kt_verify_otp_submit'
          className='btn btn-primary me-4'
          disabled={expired || loading || resending || formik.values.code.length !== 4}
        >
          <span className='indicator-label'>{formatMessage({id: 'verify'})}</span>
          {loading && (
            <span className='indicator-progress'>
              {formatMessage({id: 'please_wait'})}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
        <button
          type='button'
          className='btn btn-light'
          onClick={handleResend}
          disabled={loading || resending || !email}
        >
          {resending
            ? formatMessage({id: 'please_wait'})
            : formatMessage({id: 'AUTH.VERIFY.REQUEST_NEW'})}
        </button>
      </div>
    </form>
  )
}
